<script>
    // Licensed under Gnu Public Licence version 2
    // Copyright (c) 2020 (original work) Open Assessment Technologies SA ;

    /**
     * Component is used to render option select as a Likert scale (https://en.wikipedia.org/wiki/Likert_scale)
     * @typedef {Object} Option
     * @property {Object} value value of an option
     * @property {String} [label]
     */

    /**
     * Component is used to render option select as a Likert scale (https://en.wikipedia.org/wiki/Likert_scale)
     * @property {Boolean} labelAbove - brings labels of options above, false by default
     * @property {Option[]} options - options of likert {label, value}
     * @property {Object} [value] - chosen option value
     * @property {string} [name] - name for underlying option group
     * @fires  change with new value assigned to event details
     */
    import { createEventDispatcher } from 'svelte';
    import { generateElementId } from '@oat-sa-private/ui-core';

    export let options = [];
    export let labelAbove = false;
    export let value;
    export let name = generateElementId('likert');

    const dispatch = createEventDispatcher();

    /**
     * Dispatches 'change' event on option value change with new value in details
     * @fires change
     */
    function handleChange() {
        dispatch('change', value);
    }
</script>

<style>
    .container {
        position: relative;
        display: flex;

        & :first-child {
            text-align: start;
            align-items: flex-start;

            /*minimum width of hitbox area is 6rem,
            for first and last option hitbox is extended by 2rem,
            so base size of first and last label is 4rem */
            flex: 1 0 4rem;

            & .hitbox {
                width: calc(100% + 2rem);
                inset-inline-end: 0;
            }
        }
        & :last-child {
            text-align: end;
            align-items: flex-end;
            flex: 1 0 4rem;

            & .hitbox {
                width: calc(100% + 2rem);
                inset-inline-start: 0;
            }
        }

        /* Connector line*/
        &:before {
            content: '';
            position: absolute;
            height: 0.25rem;
            width: calc(100% - 4rem);
            left: 2rem;
            right: 2rem;
            background-color: var(--color-border-default);
        }

        &:not(.label-above) {
            &:before {
                top: 2.875rem;
            }
        }

        &.label-above {
            &:before {
                bottom: 2.875rem;
            }

            & .likert-option {
                flex-direction: column-reverse;
            }

            & .radio {
                margin-top: 1rem;
                margin-bottom: 2rem;
            }
        }
    }

    .likert-option {
        display: flex;
        flex-direction: column;
        align-items: center;
        flex: 2 0 6rem;
        position: relative;
        text-align: center;

        &:first-child {
            text-align: left;
        }

        &:last-child {
            text-align: right;
        }

        /* radio button representation */
        & .radio {
            position: relative;
            width: 2rem;
            height: 2rem;
            border-radius: var(--radius-circular);
            border: 0.25rem var(--color-border-default) solid;
            background-color: var(--color-bg-default);
            margin-top: 2rem;
            margin-bottom: 1rem;
        }

        /* hitbox overlay is used to handle clicks and to show focus-visible outline */
        & .hitbox {
            position: absolute;
            width: 100%;
            height: 100%;
            cursor: pointer;
            z-index: var(--layer-1);

            &:hover {
                & ~ .radio {
                    border-color: var(--color-brand-hover);
                    &:before {
                        background-color: var(--color-brand-hover);
                    }
                }
            }
        }

        /* visually hidden native input is used to handle logic */
        & input {
            @add-mixin visually-hidden;

            &:checked ~ .radio:before {
                content: '';
                position: absolute;
                width: 1rem;
                height: 1rem;
                border-radius: var(--radius-circular);
                background-color: var(--color-border-default);
                left: 0.25rem;
                top: 0.25rem;
            }
        }
    }

    /* display outline for keyboard-focused options */
    :global(.likert-option input:focus-visible) {
        & ~ .hitbox {
            @add-mixin simple-outline var(--color-border-focus), 0;
        }

        & ~ .radio {
            border-color: var(--color-brand-hover);
            &:before {
                background-color: var(--color-brand-hover);
            }
        }
    }
</style>

<div class="container" class:label-above={labelAbove}>
    {#each options as option (option.value)}
        <label class="likert-option">
            <input type="radio" bind:group={value} {name} value={option.value} on:change={handleChange} />
            <span class="hitbox" />
            <span class="radio" />
            <slot {option}><span>{option.label}</span></slot>
        </label>
    {/each}
</div>
